import { Link, useLocation } from "react-router-dom";
import { clsx } from "clsx";
import {
  LayoutDashboard,
  Users,
  Building2,
  Package,
  FileText,
  Settings,
  LogOut,
  ChevronLeft,
  ChevronRight,
  X,
  UserCircle,
} from "lucide-react";
import { useState, useEffect } from "react";
import { useAuthStore } from "@/stores/authStore";

interface SidebarProps {
  isOpen?: boolean;
  onClose?: () => void;
  collapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
}

const menuItems = [
  {
    path: "/admin",
    icon: LayoutDashboard,
    label: "Dashboard",
    roles: ["super_admin", "admin", "manager", "seller"],
  },
  {
    path: "/admin/companies",
    icon: Building2,
    label: "Empresas",
    roles: ["super_admin"],
  },
  {
    path: "/admin/clients",
    icon: Users,
    label: "Clientes",
    roles: ["super_admin", "admin", "manager", "seller"],
  },
  {
    path: "/admin/products",
    icon: Package,
    label: "Produtos",
    roles: ["super_admin", "admin", "manager"],
  },
  {
    path: "/admin/budgets",
    icon: FileText,
    label: "Orçamentos",
    roles: ["super_admin", "admin", "manager", "seller"],
  },
  {
    path: "/admin/users",
    icon: UserCircle,
    label: "Usuários",
    roles: ["super_admin", "admin"],
  },
  {
    path: "/admin/settings",
    icon: Settings,
    label: "Configurações",
    roles: ["super_admin", "admin"],
  },
];

export default function Sidebar({
  isOpen,
  onClose,
  collapsed: controlledCollapsed,
  onCollapsedChange,
}: SidebarProps) {
  const [internalCollapsed, setInternalCollapsed] = useState(false);
  const collapsed = controlledCollapsed ?? internalCollapsed;
  const setCollapsed = (value: boolean) => {
    setInternalCollapsed(value);
    onCollapsedChange?.(value);
  };
  const location = useLocation();
  const { user, logout } = useAuthStore();

  // Close sidebar on route change (mobile)
  useEffect(() => {
    if (onClose) onClose();
  }, [location.pathname]);

  const filteredMenu = menuItems.filter((item) =>
    item.roles.includes(user?.role || ""),
  );

  const handleLogout = () => {
    logout();
    window.location.href = "/login";
  };

  return (
    <aside
      className={clsx(
        "fixed left-0 top-0 h-full bg-bg-secondary/95 backdrop-blur-xl border-r border-white/10 transition-all duration-300 z-40",
        // Desktop
        "lg:translate-x-0",
        collapsed ? "lg:w-20" : "lg:w-64",
        // Mobile
        "w-64",
        isOpen ? "translate-x-0" : "-translate-x-full",
      )}
    >
      {/* Logo */}
      <div className="h-16 flex items-center justify-between px-4 border-b border-white/10">
        {!collapsed && (
          <Link
            to="/admin"
            className="font-script text-2xl text-accent-primary"
          >
            VintageLab
          </Link>
        )}

        {/* Close button (mobile) */}
        <button
          onClick={onClose}
          className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors lg:hidden ml-auto"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Collapse button (desktop) */}
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="p-2 text-text-muted hover:text-text-primary hover:bg-white/5 rounded-lg transition-colors hidden lg:block ml-auto"
        >
          {collapsed ? (
            <ChevronRight className="w-5 h-5" />
          ) : (
            <ChevronLeft className="w-5 h-5" />
          )}
        </button>
      </div>

      {/* Menu */}
      <nav className="p-4 space-y-2 overflow-y-auto max-h-[calc(100vh-180px)]">
        {filteredMenu.map((item) => {
          const isActive =
            location.pathname === item.path ||
            (item.path !== "/admin" && location.pathname.startsWith(item.path));

          return (
            <Link
              key={item.path}
              to={item.path}
              className={clsx(
                "flex items-center gap-3 px-4 py-3 rounded-xl transition-all duration-300",
                isActive
                  ? "bg-accent-primary/10 text-accent-primary"
                  : "text-text-secondary hover:bg-white/5 hover:text-text-primary",
              )}
            >
              <item.icon
                className={clsx(
                  "w-5 h-5 flex-shrink-0",
                  collapsed && "lg:mx-auto",
                )}
              />
              <span className={clsx("font-medium", collapsed && "lg:hidden")}>
                {item.label}
              </span>
            </Link>
          );
        })}
      </nav>

      {/* User & Logout */}
      <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-white/10 bg-bg-secondary/95">
        {!collapsed && user && (
          <div className="mb-4 px-4 lg:block">
            <p className="text-sm font-medium text-text-primary truncate">
              {user.name}
            </p>
            <p className="text-xs text-text-muted truncate">{user.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className={clsx(
            "flex items-center gap-3 px-4 py-3 rounded-xl text-red-400 hover:bg-red-500/10 transition-all duration-300 w-full",
            collapsed && "lg:justify-center",
          )}
        >
          <LogOut className="w-5 h-5 flex-shrink-0" />
          <span className={clsx("font-medium", collapsed && "lg:hidden")}>
            Sair
          </span>
        </button>
      </div>
    </aside>
  );
}
